'use client'

import { IIconProps, Icon } from '@/common/components/Icon'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Eye, EyeOff, Loader } from 'lucide-react'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'

export interface TextInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  withClear?: boolean
  clearFunc?: () => void
  required?: boolean
  outline?: boolean
  iconBefore?: IIconProps
  iconAfter?: IIconProps
  error?: string
  loading?: boolean
  wrapperClassName?: string
  prefix?: string
  inlineEdit?: boolean
  labelClassName?: string
  prefixClassName?: string
  prefixAlwaysVisible?: boolean
  alwaysShowClearWhenValue?: boolean
  copy?: boolean
  clearClassName?: string
  showError?: boolean
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      required,
      type,
      withClear = true,
      className,
      outline,
      iconBefore,
      iconAfter,
      error,
      clearFunc,
      loading,
      onFocus,
      onBlur,
      wrapperClassName,
      prefix,
      inlineEdit,
      labelClassName,
      prefixClassName,
      prefixAlwaysVisible,
      alwaysShowClearWhenValue,
      copy,
      clearClassName,
      showError = false,
      ...props
    }: TextInputProps,
    forwardedRef,
  ) => {
    const [showPassword, setShowPassword] = useState(false)
    const [focused, setFocused] = useState(false)

    const [isEditing, setIsEditing] = useState(false)
    const [copied, setCopied] = useState(false)

    const ref = useRef<HTMLInputElement>(null)

    useImperativeHandle(forwardedRef, () => ref.current as HTMLInputElement)

    return (
      <div className={cn('grid w-full gap-1', wrapperClassName)}>
        {label && (
          <Label
            className={cn('text-body-medium text-monochrome-2', labelClassName)}
          >
            {label}
            {required && <span className="text-red-500">*</span>}
          </Label>
        )}
        <div className="relative">
          {iconBefore && (
            <Icon
              className={cn(
                'absolute left-4 ',
                outline ? 'top-1/2 -translate-y-1/2' : 'h-[36px]',
                iconBefore.className,
              )}
              icon={iconBefore.icon}
              width={iconBefore.width}
              height={iconBefore.height}
              viewBox={iconBefore.viewBox}
            />
          )}
          {prefix && (prefixAlwaysVisible || props.value) && (
            <div
              className={cn(
                'absolute text-monochrome1 left-0',
                outline ? 'top-1/2 -translate-y-1/2' : '',
                prefixClassName,
                props.disabled && 'opacity-50',
              )}
            >
              <span className={`text-subtitle-m ${outline ? '' : 'h-[36px]'}`}>
                {prefix}
              </span>
            </div>
          )}
          <Input
            ref={ref}
            type={type === 'password' && showPassword ? 'text' : type}
            readOnly={inlineEdit && !isEditing}
            className={cn(
              '!bg-transparent autofill:bg-transparent autofill:text-monochrome-1 px-0 !text-subtitle-m border-x-0 border-t-0 border-b-monochrome-2/40 rounded-none focus-visible:ring-0 focus-visible:!border-b-accent-1 focus-visible:ring-offset-0 text-monochrome-1 text-base font-medium placeholder:font-semibold placeholder:text-base placeholder:text-monochrome-2 pt-0 pb-3 h-[36px]',
              outline &&
                'bg-monochrome-360 autofill:bg-monochrome-360 autofill:text-monochrome-1 rounded-[30px] !border border-monochrome-120 px-4 py-2',
              className,
              prefix && (prefixAlwaysVisible || props.value) && 'ps-10',
              iconBefore && 'ps-10',
              iconAfter && 'pe-10',
              error && '!border-accent-3 focus-visible:!border-accent-3',
            )}
            onFocus={(e) => {
              setFocused(true)
              onFocus?.(e)
            }}
            onBlur={(e) => {
              setTimeout(() => {
                setFocused(false)
              }, 200)
              if (inlineEdit && isEditing) {
                setIsEditing(false)
              }
              onBlur?.(e)
            }}
            {...props}
          />
          <div
            className={`absolute right-4 ${
              outline
                ? 'top-1/2 -translate-y-1/2'
                : `top-${
                    (24 -
                      (!iconAfter?.height ? 0 : parseInt(iconAfter.height))) /
                    2
                  }`
            }`}
          >
            {loading && (
              <Loader className="animate-spin origin-center text-monochrome1/60" />
            )}
            {iconAfter && (
              <Icon className={cn(iconAfter.className)} {...iconAfter} />
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setIsEditing(true)
              ref.current?.focus()
            }}
            className={cn(
              'hidden absolute right-0 cursor-pointer text-monochrome2',
              outline ? 'top-1/2 -translate-y-1/2' : 'top-[3px]',
              inlineEdit && !isEditing && 'block',
              outline && 'right-2',
            )}
          >
            <Icon
              icon="edit-square"
              width="18"
              height="18"
              viewBox="0 0 18 18"
            />
          </button>

          <div
            onClick={() => {
              clearFunc?.()
              props.onChange?.({
                target: {
                  value: '',
                },
              } as React.ChangeEvent<HTMLInputElement>)
              ref.current?.focus()
            }}
            className={cn(
              'hidden absolute right-0 cursor-pointer fill-monochrome2',
              outline ? 'top-1/2 -translate-y-1/2' : 'top-[3px]',
              inlineEdit && !loading && isEditing && focused && 'block',
              outline && 'right-2',
            )}
          >
            <Icon icon="clear" width="18" height="18" viewBox="0 0 18 18" />
          </div>

          {!inlineEdit &&
            !loading &&
            withClear &&
            (focused || (alwaysShowClearWhenValue && props.value)) && (
              <div
                onClick={() => {
                  clearFunc?.()
                  props.onChange?.({
                    target: {
                      value: '',
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                  ref.current?.focus()
                }}
                className={cn(
                  'absolute cursor-pointer fill-monochrome2',
                  outline ? 'top-1/2 -translate-y-1/2' : 'top-[3px]',
                  outline && 'right-2',
                  type === 'password' ? 'right-10' : 'right-0',
                  clearClassName,
                )}
              >
                <Icon icon="clear" width="18" height="18" viewBox="0 0 18 18" />
              </div>
            )}
          {type === 'password' && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={cn(
                `absolute right-2 ${
                  outline ? 'top-1/2 -translate-y-1/2' : 'top-[2px]'
                } text-slate-400 hover:text-slate-300`,
                outline && 'right-2',
              )}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          )}
          {copy && (
            <button
              type="button"
              onClick={() => {
                if (props.value) {
                  navigator.clipboard.writeText(props.value.toString())
                  setCopied(true)
                  setTimeout(() => setCopied(false), 2000)
                }
              }}
              className={cn(
                'absolute right-0 cursor-pointer text-monochrome2 hover:text-monochrome1 transition-all duration-200',
                outline ? 'top-1/2 -translate-y-1/2 right-4' : 'top-[3px]',
                copied && 'scale-110 text-green-500',
              )}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          )}
        </div>
        {showError && error && (
          <span className="text-xs text-accent-3 mt-1">{error}</span>
        )}
      </div>
    )
  },
)

TextInput.displayName = 'TextInput'

export default TextInput
